/**
 * /desktop-auth?challenge=…&state=…&device=… — confirms desktop sign-in in the browser (web-only page; not a
 * workspace tab and not translated). Never redirects without a click; the code is bound to the app's PKCE challenge.
 */
import type { WorkspaceCache } from "@bundle/core";
import { errorKind } from "@bundle/core";
import { Button, Icon, Logo, Stack, Surface, Text, useSession, useT } from "@bundle/ui";
import { useEffect, useState, type ReactNode } from "react";
import type { Container } from "../platform/container.ts";
import { openSignup } from "../platform/signupRoute.ts";
import { SignInScreen } from "../screens/SignInScreen.tsx";

const CHALLENGE = /^[A-Za-z0-9_-]{43}$/;
const COPY_FALLBACK_MS = 3000;

export type DesktopAuthParams = { challenge: string; state: string; device: string };

/** Parses and validates the query; undefined when the link is malformed. */
export function parseDesktopAuth(search: string): DesktopAuthParams | undefined {
  const q = new URLSearchParams(search);
  const challenge = q.get("challenge") ?? "";
  const state = q.get("state") ?? "";
  const device = (q.get("device") ?? "").trim().slice(0, 120);
  if (!CHALLENGE.test(challenge) || state === "" || state.length > 512) return undefined;
  return { challenge, state, device: device === "" ? "Bundle for desktop" : device };
}

type Phase =
  | { type: "confirm" }
  | { type: "working" }
  | { type: "redirected"; code: string; showCopy: boolean; copied: boolean }
  | { type: "error"; reason: "invalid" | "offline" | "failed" };

function Card({ icon, tone = "accent", logo = false, children }: { icon: Parameters<typeof Icon>[0]["name"]; tone?: "accent" | "danger" | "success"; logo?: boolean; children: ReactNode }) {
  return (
    <Stack grow align="center" justify="center" padding="6" background="bg.window" testID="desktop-auth">
      <Surface material="elevated" radius="xl" padding="8">
        <Stack gap="5">
          <Stack direction="horizontal" align="center">
            {logo ? (
              <Logo variant="lockup" size="avatar.md" />
            ) : (
              <Stack padding="2.5" radius="lg" background={tone === "danger" ? "external.bg" : tone === "success" ? "accent.subtle" : "accent.default"}>
                <Icon name={icon} size="icon.lg" color={tone === "danger" ? "status.danger" : tone === "success" ? "status.success" : "accent.fg"} />
              </Stack>
            )}
          </Stack>
          {children}
        </Stack>
      </Surface>
    </Stack>
  );
}

export function DesktopAuthPage({ container }: { container: Container }) {
  const t = useT();
  const { vm, state } = useSession();
  const [params] = useState(() => parseDesktopAuth(location.search));
  const [phase, setPhase] = useState<Phase>(params === undefined ? { type: "error", reason: "invalid" } : { type: "confirm" });
  const session = state.session;

  // Offer the copy-code fallback a few seconds after the bundle:// redirect.
  const redirected = phase.type === "redirected" && !phase.showCopy;
  useEffect(() => {
    if (!redirected) return;
    const id = setTimeout(() => setPhase((p) => (p.type === "redirected" ? { ...p, showCopy: true } : p)), COPY_FALLBACK_MS);
    return () => clearTimeout(id);
  }, [redirected]);

  if (params === undefined || (phase.type === "error" && phase.reason === "invalid")) {
    return (
      <Card icon="alert" tone="danger">
        <Stack gap="1">
          <Text variant="title3" weight="bold">
            {t("desktopAuth.invalid")}
          </Text>
          <Text color="text.secondary">{t("desktopAuth.invalidBody")}</Text>
        </Stack>
      </Card>
    );
  }
  if (state.status !== "signedIn" || session === undefined) {
    // Sign in here and come back to this exact URL (an OAuth redirect returns to it too).
    const back = `${location.pathname}${location.search}`;
    return <SignInScreen returnTo={back} defaultDevEmail={container.mock ? "ada@acme.test" : ""} mock={container.mock} onCreateAccount={() => openSignup(vm, back)} />;
  }

  const cache = session.workspace === undefined ? undefined : container.queryClient.getQueryData<WorkspaceCache>(["ws", session.workspace.id, "bootstrap"]);
  const name = cache?.members[session.userId]?.displayName ?? session.email.split("@")[0] ?? session.email;

  const open = async () => {
    setPhase({ type: "working" });
    try {
      const { code } = await container.api.desktopAuthCode(params.challenge, params.device);
      setPhase({ type: "redirected", code, showCopy: false, copied: false });
      window.location.href = `bundle://auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(params.state)}`;
    } catch (e) {
      const kind = errorKind(e);
      if (kind === "auth") {
        // The browser session expired: sign in again, then confirm again.
        setPhase({ type: "confirm" });
        void vm.authRejected();
      } else setPhase({ type: "error", reason: kind === "network" ? "offline" : kind === "invalid" || kind === "notFound" ? "invalid" : "failed" });
    }
  };

  if (phase.type === "redirected") {
    return (
      <Card icon="checkCircle" tone="success">
        <Stack gap="1">
          <Text variant="title3" weight="bold">
            {t("desktopAuth.redirected")}
          </Text>
          <Text color="text.secondary">{t("desktopAuth.redirectedBody")}</Text>
        </Stack>
        {phase.showCopy ? (
          <Stack gap="2">
            <Button
              label={t("desktopAuth.copyCode")}
              icon="copy"
              variant="secondary"
              onPress={() => {
                void navigator.clipboard.writeText(phase.code).then(() => setPhase({ ...phase, copied: true }));
              }}
              testID="desktop-auth-copy"
            />
            {phase.copied ? (
              <Text variant="footnote" color="status.success">
                {t("desktopAuth.copied")}
              </Text>
            ) : null}
          </Stack>
        ) : null}
      </Card>
    );
  }

  return (
    <Card icon="window" logo>
      <Stack gap="1">
        <Text variant="title2" weight="bold">
          {t("desktopAuth.title")}
        </Text>
        <Text color="text.secondary" testID="desktop-auth-identity">
          {t("desktopAuth.signedInAs", { name, email: session.email })}
        </Text>
        <Text variant="footnote" color="text.tertiary">
          {t("desktopAuth.device", { device: params.device })}
        </Text>
      </Stack>
      {phase.type === "error" ? (
        <Stack direction="horizontal" align="center" gap="2">
          <Icon name={phase.reason === "offline" ? "offline" : "alert"} size="icon.sm" color="status.danger" />
          <Text variant="footnote" color="status.danger">
            {phase.reason === "offline" ? t("desktopAuth.offline") : t("desktopAuth.failed")}
          </Text>
        </Stack>
      ) : null}
      <Stack gap="2">
        <Button label={t("desktopAuth.open")} icon="externalLink" variant="primary" size="lg" loading={phase.type === "working"} onPress={() => void open()} testID="desktop-auth-open" />
        <Button label={t("desktopAuth.switchAccount")} variant="ghost" onPress={() => vm.signOut()} testID="desktop-auth-switch" />
      </Stack>
    </Card>
  );
}
